// guest — the QR menu a diner opens at the table, plus the guest sign-in page.
// See README.md. Keys must exist in BOTH maps (checked by npm run check:locales).
//
// This is the one surface a member of the public reads, so the French matters
// most here: a diner in Mauritius meets the product before any staff screen.
export const en = {
  // --- Booking page: a table held while paying ---------------------------
  'guest.reserve.heldLabel': 'Held — pay to book',
  'guest.reserve.tableHoldHint': 'Your table is held for {n} minutes while you pay, and paying books it — the restaurant approves nothing. Let the time run out and the table is released.',
  // --- Dish sheet: the pairings set on the dish -----------------------------
  'guest.picker.pairs': 'Goes well with',
  'guest.picker.from': 'from {price}',

  // --- Guest sign-in (the QR landing) --------------------------------------
  'guest.login.opening': 'Opening your table…',
  'guest.login.qrOnly': 'This page is meant to be opened by scanning a table QR code.',

  // --- Order status, whole order -------------------------------------------
  'guest.menu.orderStatus.sent': 'Sent to the kitchen',
  'guest.menu.orderStatus.confirmed': 'Confirmed by the kitchen',
  'guest.menu.orderStatus.preparing': 'Being prepared',
  'guest.menu.orderStatus.ready': 'Ready — coming to your table',
  // Counter / no table: the guest fetches it, so nothing is promised to a seat.
  'guest.menu.orderStatus.readyCollect': 'Ready — collect at the counter',
  'guest.menu.orderStatus.delivered': 'Served — enjoy!',
  'guest.menu.orderStatus.completed': 'Served',
  'guest.menu.orderStatus.cancelled': 'Cancelled',

  // --- Order status, per dish ----------------------------------------------
  // One order's dishes travel separately, so each line reports its own state.
  'guest.menu.itemStatus.held': 'Held for a later course',
  'guest.menu.itemStatus.sent': 'Sent to the kitchen',
  'guest.menu.itemStatus.cooking': 'Being cooked',
  'guest.menu.itemStatus.ready': 'Ready — coming to your table',
  'guest.menu.itemStatus.readyCollect': 'Ready — collect at the counter',
  'guest.menu.itemStatus.served': 'Served',
  'guest.menu.itemStatus.servedAt': 'Served {time}',
  'guest.menu.itemStatus.someServed': '{served} of {total} served',

  // --- "Your food is ready" alert ------------------------------------------
  // Pushed to the phone the moment the kitchen plates a dish, whatever tab the
  // guest is on — so it is written to be read at a glance, standing up.
  'guest.menu.ready.orderTitle': 'Your order is ready!',
  'guest.menu.ready.dishTitleOne': 'Your dish is ready!',
  'guest.menu.ready.dishTitleMany': 'Your dishes are ready!',
  'guest.menu.ready.onItsWayTable': 'On its way to table {code}.',
  'guest.menu.ready.onItsWay': 'On its way to your table.',
  'guest.menu.ready.collect': 'Please collect it from the counter.',
  'guest.menu.ready.view': 'View my order',
  // Shared by both notices (ready / cancelled), hence the neutral names.
  'guest.menu.notice.orderLine': 'Order {number}',
  'guest.menu.notice.dismiss': 'Got it',

  // --- The order was pulled -----------------------------------------------
  'guest.menu.cancelled.title': 'Your order was cancelled',
  'guest.menu.cancelled.counter': 'Please reach out to the counter — they can tell you why and put it through again.',
  'guest.menu.cancelled.staff': 'Please speak to a member of staff — they can tell you why and put it through again.',

  // --- Browsing a KIOSK menu without signing in ----------------------------
  // The counter's QR opened with "just browse": the way back into ordering.
  'guest.menu.browse.kioskInvite': 'Sign in to order from this counter — your dishes go straight to the kitchen.',
  'guest.menu.browse.signInCta': 'Sign in & order',
  'guest.menu.browse.signInAndPlace': 'SIGN IN & PLACE ORDER',
  'guest.menu.browse.signInNote': 'You’ll sign in to send this order — your cart is kept.',

  // --- Orders tab chrome ----------------------------------------------------
  'guest.menu.orderTotal': 'Order total',
  'guest.menu.noteLabel': 'Note',
  'guest.menu.tableSuffix': 'Table {code}',

  // --- Dish badges ----------------------------------------------------------
  'guest.menu.spicy.less': 'Less Spicy',
  'guest.menu.spicy.normal': 'Spicy',
  'guest.menu.spicy.extra': 'Extra Spicy',
  'guest.menu.diet.veg': 'Veg',
  'guest.menu.diet.vegan': 'Vegan',
  'guest.menu.diet.nonVeg': 'Non-Veg',
  'guest.menu.variant.regular': 'Regular',

  // --- Buffet counter panel -------------------------------------------------
  'guest.menu.buffet.askLine': 'Ask the kitchen for something from the counter',
  'guest.menu.buffet.onTheWay': '{count} on the way',

  // --- Standing category rail (kiosk panels and wide screens) --------------
  'guest.menu.rail.title': 'Menu',
  'guest.menu.rail.featured': 'Featured',
  'guest.menu.rail.dishes': '{count} dishes',
  'guest.menu.buffet.inProgress': 'kitchen is on it',
  'guest.menu.buffet.sent': 'sent',
  'guest.menu.buffet.noSpread': 'Today’s spread is not listed yet — please ask staff.',
  'guest.menu.buffet.askFailedTitle': 'Could not send that',
  'guest.menu.buffet.kitchenUnreachable': 'We could not reach the kitchen — please ask a member of staff.',

  // --- Loading / connection errors -----------------------------------------
  'guest.menu.error.missingToken': 'Missing customer token. Please scan the QR code again.',
  'guest.menu.error.loadMenu': 'Unable to load menu.',
  'guest.menu.error.network': 'Network error while loading the menu.',
  'guest.menu.networkErrorTitle': 'Network error',
  'guest.menu.serverUnreachable': 'Could not reach the server. Please try again or ask the staff.',
  'guest.menu.noConnection': 'No connection',
  'guest.menu.askStaff': 'Please ask a member of staff.',

  // --- Taking / sharing a table --------------------------------------------
  'guest.menu.table.occupiedTitle': 'Table {code} is occupied',
  'guest.menu.table.occupiedPlain': 'This table is occupied',
  'guest.menu.table.shareBody': 'You can share the table and order separately — you get your own orders and your own bill.{seats}',
  'guest.menu.table.shareConfirm': 'Share this table',
  'guest.menu.table.pickAnother': 'Pick another',
  'guest.menu.table.seatsLeftOne': '{count} seat remains.',
  'guest.menu.table.seatsLeftMany': '{count} seats remain.',
  'guest.menu.table.startFailed': 'Could not start a session at this table.',
  'guest.menu.table.networkError': 'Network error while selecting the table.',
  'guest.menu.table.occupiedFallback': 'This table is currently occupied. Please ask the staff.',
  'guest.menu.pax.failed': 'Could not reserve the table. Please try again.',
  'guest.menu.pax.networkError': 'Network error while reserving the table.',

  // --- Leaving the table ----------------------------------------------------
  'guest.menu.leave.title': 'Leave Table {code}?',
  'guest.menu.leave.body': 'This ends your dining session and frees the table for other guests. Any unpaid bill should be settled with the waiter first.',
  'guest.menu.leave.confirm': 'Leave table',
  'guest.menu.leave.stayShort': 'Stay',
  'guest.menu.leave.stayAtTable': 'Stay at table',
  'guest.menu.leave.onTheWayTitle': 'Your food is on the way! 🍽️',
  'guest.menu.leave.onTheWayBody': 'Your order has been sent to the kitchen and is on its way to your table. Please stay until it is served and the bill is settled — or call the waiter if you need help.',
  'guest.menu.leave.settleTitle': 'Please settle your bill first',
  'guest.menu.leave.settleBody': 'Your bill hasn’t been settled yet. Please pay the waiter before leaving the table — or call the waiter to bring the bill.',
  'guest.menu.leave.failedTitle': 'Could not leave the table',
  'guest.menu.leave.canCallWaiter': 'You can call the waiter for help.',
  'guest.menu.leave.freedTitle': 'Table freed',
  'guest.menu.leave.freedBody': 'Thanks for visiting! The table is now available for other guests.',

  // --- Bill already settled -------------------------------------------------
  'guest.menu.billPaidTitle': 'Bill already paid',
  'guest.menu.billPaidBody': 'This sitting is closed. Sign in again to start a new order.',
  'guest.menu.signInAgain': 'Sign in again',

  // --- Pre-order ------------------------------------------------------------
  'guest.menu.preorder.sessionExpired': 'Your session has expired — please sign in again to save the pre-order.',
  'guest.menu.preorder.saveFailed': 'Could not save your pre-order (server responded {status}). Please try again.',

  // --- Paying a wallet by QR (kiosk counters) -------------------------------
  // {wallet} is a brand — Juice, my.t money — and is never translated.
  'guest.menu.pay.scanTitle': 'Scan the counter’s {wallet} QR code',
  'guest.menu.pay.scanStarting': 'Starting the camera…',
  'guest.menu.pay.scanned': 'QR read — approving…',
  'guest.menu.pay.scanDemo': 'Demo — any QR code approves this payment.',
  // No camera to scan with: the guest shows their own code instead.
  'guest.menu.pay.showTitle': 'Pay with {wallet}',
  'guest.menu.pay.showCode': 'Show this code at the counter',
  'guest.menu.pay.showCodeDemo': 'Demo code — press Pay below to approve.',

  // --- The order-placed screen (after paying at the counter) ---------------
  // Read standing up, phone in one hand: short lines, and the wait and the
  // pick-up point big enough to take in at a glance.
  'guest.menu.success.title': 'Order placed successfully',
  'guest.menu.success.subtitle': 'Sit tight — we’ll shout when your food is ready.',
  'guest.menu.success.readyIn': 'Ready in',
  'guest.menu.success.minutes': '{count} min',
  'guest.menu.success.pickUpAt': 'Pick up at',
  'guest.menu.success.servedAt': 'Served at',
  'guest.menu.success.yourOrder': 'Your order',
  'guest.menu.success.paid': 'Paid',
  'guest.menu.success.step.placed': 'Placed',
  'guest.menu.success.step.cooking': 'Cooking',
  'guest.menu.success.step.ready': 'Ready',
  'guest.menu.success.rate.prompt': 'How was ordering?',
  'guest.menu.success.rate.thanks': 'Thanks for telling us!',
  'guest.menu.success.rate.bad': 'Poor',
  'guest.menu.success.rate.ok': 'Okay',
  'guest.menu.success.rate.great': 'Great',
  'guest.menu.success.track': 'Track my order',
  'guest.menu.success.addMore': 'Add something else',

  // --- Small shared words ---------------------------------------------------
  'guest.menu.callWaiter': 'Call waiter',
  'guest.menu.ok': 'OK',
  'guest.menu.close': 'Close',
  'guest.menu.notNow': 'Not now',

  // --- Join our rewards (the loyalty sign-up on the QR menu) ---------------
  'guest.rewards.cardTitle': 'Join our rewards',
  'guest.rewards.cardTitleNamed': 'Join {name}',
  'guest.rewards.cardBody': 'Earn points every visit and turn them into treats.',
  'guest.rewards.cardCta': 'Join free',
  'guest.rewards.firstName': 'First name',
  'guest.rewards.lastName': 'Last name (optional)',
  'guest.rewards.phone': 'Mobile number',
  'guest.rewards.email': 'Email (optional)',
  'guest.rewards.birthday': 'Birthday — for a treat on us (optional)',
  'guest.rewards.marketing': 'Tell me about offers and events',
  'guest.rewards.join': 'Join',
  'guest.rewards.joining': 'Joining…',
  'guest.rewards.doneTitle': 'You’re in!',
  'guest.rewards.doneBody': 'The team will confirm your membership at the counter — your points start with your next bill.',
  'guest.rewards.needName': 'Please give your first name.',
  'guest.rewards.needPhone': 'Please give a valid mobile number.',
  'guest.rewards.failed': 'Could not sign you up — please try again.',
};

export const fr = {
  'guest.reserve.heldLabel': 'Retenue — payez pour réserver',
  'guest.reserve.tableHoldHint': 'Votre table est retenue {n} minutes le temps de payer, et le paiement la réserve — le restaurant n’a rien à approuver. Passé ce délai, la table est libérée.',
  'guest.picker.pairs': 'Se marie bien avec',
  'guest.picker.from': 'à partir de {price}',

  // --- Guest sign-in (the QR landing) --------------------------------------
  'guest.login.opening': 'Ouverture de votre table…',
  'guest.login.qrOnly': 'Cette page s\'ouvre en scannant le code QR d\'une table.',

  // --- Order status, whole order -------------------------------------------
  'guest.menu.orderStatus.sent': 'Envoyée en cuisine',
  'guest.menu.orderStatus.confirmed': 'Confirmée par la cuisine',
  'guest.menu.orderStatus.preparing': 'En préparation',
  'guest.menu.orderStatus.ready': 'Prête — elle arrive à votre table',
  'guest.menu.orderStatus.readyCollect': 'Prête — à retirer au comptoir',
  'guest.menu.orderStatus.delivered': 'Servie — bon appétit !',
  'guest.menu.orderStatus.completed': 'Servie',
  'guest.menu.orderStatus.cancelled': 'Annulée',

  // --- Order status, per dish ----------------------------------------------
  'guest.menu.itemStatus.held': 'Gardé pour un service suivant',
  'guest.menu.itemStatus.sent': 'Envoyé en cuisine',
  'guest.menu.itemStatus.cooking': 'En cuisson',
  'guest.menu.itemStatus.ready': 'Prêt — il arrive à votre table',
  'guest.menu.itemStatus.readyCollect': 'Prêt — à retirer au comptoir',
  'guest.menu.itemStatus.served': 'Servi',
  'guest.menu.itemStatus.servedAt': 'Servi à {time}',
  'guest.menu.itemStatus.someServed': '{served} plat(s) servi(s) sur {total}',

  // --- "Your food is ready" alert ------------------------------------------
  'guest.menu.ready.orderTitle': 'Votre commande est prête !',
  'guest.menu.ready.dishTitleOne': 'Votre plat est prêt !',
  'guest.menu.ready.dishTitleMany': 'Vos plats sont prêts !',
  'guest.menu.ready.onItsWayTable': 'En route vers la table {code}.',
  'guest.menu.ready.onItsWay': 'En route vers votre table.',
  'guest.menu.ready.collect': 'À retirer au comptoir, merci.',
  'guest.menu.ready.view': 'Voir ma commande',
  'guest.menu.notice.orderLine': 'Commande {number}',
  'guest.menu.notice.dismiss': 'Compris',

  // --- The order was pulled -----------------------------------------------
  'guest.menu.cancelled.title': 'Votre commande a été annulée',
  'guest.menu.cancelled.counter': 'Merci de vous adresser au comptoir — on vous expliquera pourquoi et on pourra la repasser.',
  'guest.menu.cancelled.staff': 'Merci de vous adresser à un membre du personnel — on vous expliquera pourquoi et on pourra la repasser.',

  // --- Browsing a KIOSK menu without signing in ----------------------------
  'guest.menu.browse.kioskInvite': 'Connectez-vous pour commander à ce comptoir — vos plats partent directement en cuisine.',
  'guest.menu.browse.signInCta': 'Se connecter et commander',
  'guest.menu.browse.signInAndPlace': 'SE CONNECTER ET COMMANDER',
  'guest.menu.browse.signInNote': 'Vous vous connecterez pour envoyer cette commande — votre panier est conservé.',

  // --- Orders tab chrome ----------------------------------------------------
  'guest.menu.orderTotal': 'Total de la commande',
  'guest.menu.noteLabel': 'Note',
  'guest.menu.tableSuffix': 'Table {code}',

  // --- Dish badges ----------------------------------------------------------
  'guest.menu.spicy.less': 'Peu épicé',
  'guest.menu.spicy.normal': 'Épicé',
  'guest.menu.spicy.extra': 'Très épicé',
  'guest.menu.diet.veg': 'Végé',
  'guest.menu.diet.vegan': 'Végan',
  'guest.menu.diet.nonVeg': 'Non végé',
  'guest.menu.variant.regular': 'Normal',

  // --- Buffet counter panel -------------------------------------------------
  'guest.menu.buffet.askLine': 'Demandez un plat du buffet à la cuisine',
  'guest.menu.buffet.onTheWay': '{count} en route',

  // --- Rail des catégories (bornes et grands écrans) -----------------------
  'guest.menu.rail.title': 'Carte',
  'guest.menu.rail.featured': 'À la une',
  'guest.menu.rail.dishes': '{count} plats',
  'guest.menu.buffet.inProgress': 'la cuisine s\'en occupe',
  'guest.menu.buffet.sent': 'envoyé',
  'guest.menu.buffet.noSpread': 'Le buffet du jour n\'est pas encore affiché — demandez au personnel.',
  'guest.menu.buffet.askFailedTitle': 'Impossible d\'envoyer la demande',
  'guest.menu.buffet.kitchenUnreachable': 'Nous n\'avons pas pu joindre la cuisine — adressez-vous au personnel.',

  // --- Loading / connection errors -----------------------------------------
  'guest.menu.error.missingToken': 'Jeton client manquant. Veuillez scanner à nouveau le code QR.',
  'guest.menu.error.loadMenu': 'Impossible de charger le menu.',
  'guest.menu.error.network': 'Erreur réseau lors du chargement du menu.',
  'guest.menu.networkErrorTitle': 'Erreur réseau',
  'guest.menu.serverUnreachable': 'Serveur injoignable. Réessayez ou adressez-vous au personnel.',
  'guest.menu.noConnection': 'Pas de connexion',
  'guest.menu.askStaff': 'Veuillez vous adresser au personnel.',

  // --- Taking / sharing a table --------------------------------------------
  'guest.menu.table.occupiedTitle': 'La table {code} est occupée',
  'guest.menu.table.occupiedPlain': 'Cette table est occupée',
  'guest.menu.table.shareBody': 'Vous pouvez partager la table et commander séparément — vous aurez vos propres commandes et votre propre addition.{seats}',
  'guest.menu.table.shareConfirm': 'Partager cette table',
  'guest.menu.table.pickAnother': 'Choisir une autre',
  'guest.menu.table.seatsLeftOne': 'Il reste {count} place.',
  'guest.menu.table.seatsLeftMany': 'Il reste {count} places.',
  'guest.menu.table.startFailed': 'Impossible d\'ouvrir une session à cette table.',
  'guest.menu.table.networkError': 'Erreur réseau lors de la sélection de la table.',
  'guest.menu.table.occupiedFallback': 'Cette table est actuellement occupée. Adressez-vous au personnel.',
  'guest.menu.pax.failed': 'Impossible de réserver la table. Veuillez réessayer.',
  'guest.menu.pax.networkError': 'Erreur réseau lors de la réservation de la table.',

  // --- Leaving the table ----------------------------------------------------
  'guest.menu.leave.title': 'Quitter la table {code} ?',
  'guest.menu.leave.body': 'Cela met fin à votre repas et libère la table pour d\'autres clients. Toute addition impayée doit d\'abord être réglée auprès du serveur.',
  'guest.menu.leave.confirm': 'Quitter la table',
  'guest.menu.leave.stayShort': 'Rester',
  'guest.menu.leave.stayAtTable': 'Rester à table',
  'guest.menu.leave.onTheWayTitle': 'Votre commande arrive ! 🍽️',
  'guest.menu.leave.onTheWayBody': 'Votre commande a été envoyée en cuisine et arrive à votre table. Merci de rester jusqu\'à ce qu\'elle soit servie et l\'addition réglée — ou appelez le serveur si besoin.',
  'guest.menu.leave.settleTitle': 'Veuillez d\'abord régler votre addition',
  'guest.menu.leave.settleBody': 'Votre addition n\'a pas encore été réglée. Merci de payer le serveur avant de quitter la table — ou appelez le serveur pour qu\'il vous l\'apporte.',
  'guest.menu.leave.failedTitle': 'Impossible de quitter la table',
  'guest.menu.leave.canCallWaiter': 'Vous pouvez appeler le serveur pour vous aider.',
  'guest.menu.leave.freedTitle': 'Table libérée',
  'guest.menu.leave.freedBody': 'Merci de votre visite ! La table est de nouveau disponible.',

  // --- Bill already settled -------------------------------------------------
  'guest.menu.billPaidTitle': 'Addition déjà réglée',
  'guest.menu.billPaidBody': 'Ce repas est clôturé. Reconnectez-vous pour passer une nouvelle commande.',
  'guest.menu.signInAgain': 'Se reconnecter',

  // --- Pre-order ------------------------------------------------------------
  'guest.menu.preorder.sessionExpired': 'Votre session a expiré — reconnectez-vous pour enregistrer la pré-commande.',
  'guest.menu.preorder.saveFailed': 'Impossible d\'enregistrer votre pré-commande (le serveur a répondu {status}). Veuillez réessayer.',

  // --- Paying a wallet by QR (kiosk counters) -------------------------------
  'guest.menu.pay.scanTitle': 'Scannez le code QR {wallet} au comptoir',
  'guest.menu.pay.scanStarting': 'Démarrage de la caméra…',
  'guest.menu.pay.scanned': 'QR lu — validation en cours…',
  'guest.menu.pay.scanDemo': 'Démo — n’importe quel code QR valide ce paiement.',
  'guest.menu.pay.showTitle': 'Payer avec {wallet}',
  'guest.menu.pay.showCode': 'Présentez ce code au comptoir',
  'guest.menu.pay.showCodeDemo': 'Code de démo — appuyez sur Payer ci-dessous pour valider.',

  // --- The order-placed screen (after paying at the counter) ---------------
  'guest.menu.success.title': 'Commande passée avec succès',
  'guest.menu.success.subtitle': 'Installez-vous — nous vous appelons dès que c’est prêt.',
  'guest.menu.success.readyIn': 'Prêt dans',
  'guest.menu.success.minutes': '{count} min',
  'guest.menu.success.pickUpAt': 'À récupérer à',
  'guest.menu.success.servedAt': 'Servi à',
  'guest.menu.success.yourOrder': 'Votre commande',
  'guest.menu.success.paid': 'Payé',
  'guest.menu.success.step.placed': 'Passée',
  'guest.menu.success.step.cooking': 'En cuisson',
  'guest.menu.success.step.ready': 'Prête',
  'guest.menu.success.rate.prompt': 'Comment s’est passée la commande ?',
  'guest.menu.success.rate.thanks': 'Merci pour votre retour !',
  'guest.menu.success.rate.bad': 'Décevant',
  'guest.menu.success.rate.ok': 'Correct',
  'guest.menu.success.rate.great': 'Excellent',
  'guest.menu.success.track': 'Suivre ma commande',
  'guest.menu.success.addMore': 'Ajouter autre chose',

  // --- Small shared words ---------------------------------------------------
  'guest.menu.callWaiter': 'Appeler le serveur',
  'guest.menu.ok': 'OK',
  'guest.menu.close': 'Fermer',
  'guest.menu.notNow': 'Pas maintenant',

  // --- Join our rewards (the loyalty sign-up on the QR menu) ---------------
  'guest.rewards.cardTitle': 'Rejoignez notre programme de fidélité',
  'guest.rewards.cardTitleNamed': 'Rejoignez {name}',
  'guest.rewards.cardBody': 'Gagnez des points à chaque visite et transformez-les en petits plaisirs.',
  'guest.rewards.cardCta': 'Adhérer gratuitement',
  'guest.rewards.firstName': 'Prénom',
  'guest.rewards.lastName': 'Nom (facultatif)',
  'guest.rewards.phone': 'Numéro de portable',
  'guest.rewards.email': 'E-mail (facultatif)',
  'guest.rewards.birthday': 'Anniversaire — pour un cadeau de notre part (facultatif)',
  'guest.rewards.marketing': 'Tenez-moi au courant des offres et événements',
  'guest.rewards.join': 'Adhérer',
  'guest.rewards.joining': 'Inscription…',
  'guest.rewards.doneTitle': 'C’est fait !',
  'guest.rewards.doneBody': 'L’équipe confirmera votre adhésion au comptoir — vos points démarrent avec votre prochaine addition.',
  'guest.rewards.needName': 'Merci d’indiquer votre prénom.',
  'guest.rewards.needPhone': 'Merci d’indiquer un numéro de portable valide.',
  'guest.rewards.failed': 'L’inscription n’a pas abouti — veuillez réessayer.',
};
